/* Post composer › '# Hashtags' panel (Part E, T31; Hashtags.dc.html; S23).
 *
 * Saved sets (click adds the set's hashtags, skipping any already in the
 * caption), '+ Save current hashtags as a set', 'Manage sets', and AI
 * suggestions for this post (add one / add all / suggest again, 1 AI
 * credit per suggest). API: /api/hashtags/* (docs/MOBILE_API.md › Hashtags).
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

import HashtagSetManager from './HashtagSetManager';
import { addHashtags, uniqueHashtags, tagKey, TRIM_KEEP } from '../../../../utils/hashtags';

const ax = axios.create({ withCredentials: true });

const S = {
  card: { background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '18px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', gap: 16 },
  section: { display: 'flex', flexDirection: 'column', gap: 8 },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  label: { fontSize: 11, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', color: '#6b7280' },
  link: { fontSize: 13, fontWeight: 600, color: '#2563eb', background: 'none', border: 0, padding: 0, cursor: 'pointer' },
  chips: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  setChip: active => ({ padding: '8px 14px', borderRadius: 999, border: `1px solid ${active ? '#2563eb' : '#d1d5db'}`, background: active ? '#eff6ff' : '#fff', color: active ? '#1d4ed8' : '#374151', fontSize: 13, fontWeight: 600, cursor: 'pointer' }),
  saveChip: { padding: '8px 14px', borderRadius: 999, border: '1px dashed #9ca3af', background: '#fff', color: '#374151', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  tagChip: added => ({ padding: '7px 12px', borderRadius: 999, border: `1px solid ${added ? '#bbf7d0' : '#d1d5db'}`, background: added ? '#f0fdf4' : '#fff', color: added ? '#16a34a' : '#374151', fontSize: 13, fontWeight: 500, cursor: added ? 'default' : 'pointer' }),
  note: { fontSize: 13, color: '#6b7280' },
  divider: { height: 1, background: '#f1f5f9' },
  aiBtn: { padding: '5px 11px', borderRadius: 8, border: '1px solid #dbeafe', background: '#eff6ff', color: '#2563eb', fontSize: 12, fontWeight: 500, cursor: 'pointer' },
  primary: { padding: '8px 14px', borderRadius: 8, border: 0, background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  input: { flex: '1 1 180px', minWidth: 0, padding: '7px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, fontFamily: 'inherit', outline: 'none' },
  ghost: { padding: '8px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  error: { fontSize: 13, color: '#dc2626' },
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// Only a real web address goes to the AI (not a local upload preview).
const photoUrl = files => {
  const url = files && files[0] && files[0].url;
  return typeof url === 'string' && /^https?:\/\//i.test(url) ? url : null;
};

export default function HashtagPanel ({ caption, setCaption, files = [], selectedAccounts = [], settings, onSettingsChange }) {
  const [sets, setSets] = useState([]);
  const [available, setAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [activeSet, setActiveSet] = useState(null);
  const [status, setStatus] = useState('');

  const [saving, setSaving] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveError, setSaveError] = useState('');
  const [showManager, setShowManager] = useState(false);

  const [suggestions, setSuggestions] = useState([]);
  const [shown, setShown] = useState([]);           // every suggestion so far, so 'Suggest again' gives new ones
  const [suggesting, setSuggesting] = useState(false);
  const [aiError, setAiError] = useState(null);     // { message, credits }

  useEffect(() => {
    let live = true;
    ax.get('/api/hashtags/sets')
      .then(({ data }) => {
        if (!live) return;
        setSets(Array.isArray(data.sets) ? data.sets : []);
        setAvailable(data.available !== false);
      })
      .catch(() => live && setLoadError('Your saved sets could not be loaded. Try again in a moment.'))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, []);

  const inCaption = useMemo(() => new Set(uniqueHashtags(caption).map(tagKey)), [caption]);
  const captionTags = useMemo(() => uniqueHashtags(caption), [caption]);
  const remaining = suggestions.filter(t => !inCaption.has(tagKey(t)));
  const photo = photoUrl(files);

  const add = (tags, what) => {
    const out = addHashtags(caption, tags);
    if (out.added.length) setCaption(out.caption);
    const parts = [];
    parts.push(out.added.length ? `Added ${plural(out.added.length, 'hashtag', 'hashtags')}${what ? ` from ${what}` : ''}.` : 'Every hashtag is already in the caption.');
    if (out.added.length && out.skipped.length) parts.push(`${out.skipped.length} already in the caption ${out.skipped.length === 1 ? 'was' : 'were'} skipped.`);
    setStatus(parts.join(' '));
  };

  const applySet = set => {
    setActiveSet(set.id);
    add(set.tags, `“${set.name}”`);
  };

  const startSave = () => {
    setSaveError('');
    setSaveName('');
    setSaving(true);
  };

  const saveSet = async e => {
    e.preventDefault();
    // This form sits inside the composer's <form>: don't let the submit reach it (it would publish the post).
    e.stopPropagation();
    setSaveError('');
    try {
      const { data } = await ax.post('/api/hashtags/sets', { name: saveName, tags: captionTags });
      setSets(prev => [...prev, data.set].sort((a, b) => a.name.localeCompare(b.name)));
      setActiveSet(data.set.id);
      setSaving(false);
      setStatus(`Saved “${data.set.name}” with ${plural(data.set.count, 'hashtag', 'hashtags')}.`);
    } catch (err) {
      setSaveError(err?.response?.data?.message || 'The set could not be saved. Try again in a moment.');
    }
  };

  const suggest = async () => {
    setSuggesting(true);
    setAiError(null);
    try {
      const networks = [...new Set(selectedAccounts.map(a => a.platform).filter(Boolean))];
      const { data } = await ax.post('/api/hashtags/suggest', { caption, imageUrl: photo, networks, exclude: shown });
      const list = Array.isArray(data.hashtags) ? data.hashtags : [];
      setSuggestions(list);
      setShown(prev => [...prev, ...list].slice(-60));
    } catch (err) {
      const res = err?.response;
      if (res?.status === 402) setAiError({ message: 'You’ve used all your AI credits for this cycle.', credits: true });
      else setAiError({ message: res?.data?.message || 'Could not suggest hashtags right now. Try again in a moment.' });
    } finally {
      setSuggesting(false);
    }
  };

  const trimOn = !settings || settings.trimLinkedInFacebook !== false;

  return (
    <>
      <div style={S.card} data-testid='hashtag-panel'>
        {/* ── saved sets ── */}
        <div style={S.section}>
          <div style={S.head}>
            <span style={S.label}>Your saved sets</span>
            {available && <button type='button' style={S.link} onClick={() => setShowManager(true)}>Manage sets</button>}
          </div>

          {loading ? (
            <div style={S.note}>Loading your sets…</div>
          ) : loadError ? (
            <div style={S.error}>{loadError}</div>
          ) : !available ? (
            <div style={S.note}>Saving hashtag sets is coming soon. AI suggestions below already work.</div>
          ) : (
            <>
              <div style={S.chips}>
                {sets.map(set => (
                  <button key={set.id} type='button' style={S.setChip(activeSet === set.id)} aria-pressed={activeSet === set.id}
                    onClick={() => applySet(set)} title={set.tags.join(' ')}>
                    {set.name} · {set.count}
                  </button>
                ))}
                {!saving && (
                  <button type='button' style={S.saveChip} onClick={startSave}>+ Save current hashtags as a set</button>
                )}
              </div>
              {saving && (
                captionTags.length ? (
                  <form onSubmit={saveSet} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <label htmlFor='hashtag-set-name' style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Set name</label>
                    <input id='hashtag-set-name' style={S.input} value={saveName} maxLength={40} autoFocus
                      placeholder={`Name for these ${captionTags.length} hashtags`} onChange={e => setSaveName(e.target.value)} />
                    <button type='submit' style={S.primary} disabled={!saveName.trim()}>Save set</button>
                    <button type='button' style={S.ghost} onClick={() => setSaving(false)}>Cancel</button>
                  </form>
                ) : (
                  <div style={S.note}>
                    Add some hashtags to the caption first, then save them as a set.{' '}
                    <button type='button' style={S.link} onClick={() => setSaving(false)}>OK</button>
                  </div>
                )
              )}
              {saveError && <div style={S.error} role='alert'>{saveError}</div>}
              {!sets.length && !saving && <div style={S.note}>No saved sets yet. Add hashtags to your caption, then save them as a set to reuse them.</div>}
              {!!sets.length && <div style={S.note}>Click a set to add all its hashtags. Hashtags already in the caption are skipped.</div>}
            </>
          )}
          {status && <div style={{ ...S.note, color: '#16a34a' }} role='status'>{status}</div>}
        </div>

        <div style={S.divider} />

        {/* ── AI suggestions ── */}
        <div style={S.section}>
          <div style={S.head}>
            <span style={S.label}>AI suggestions for this post</span>
            <button type='button' style={{ ...S.aiBtn, opacity: suggesting ? 0.7 : 1 }} onClick={suggest} disabled={suggesting || (!caption.trim() && !photo)}>
              {suggesting ? 'Thinking…' : suggestions.length ? 'Suggest again' : '✨ Suggest'}
            </button>
          </div>

          {!suggestions.length && !aiError && (
            <div style={S.note}>
              {caption.trim() || photo
                ? 'Get up to 15 hashtags picked for this post. Uses 1 AI credit.'
                : 'Write your caption first, then get hashtags picked for it.'}
            </div>
          )}

          {!!suggestions.length && (
            <>
              <div style={S.chips}>
                {suggestions.map(tag => {
                  const added = inCaption.has(tagKey(tag));
                  return (
                    <button key={tag} type='button' style={S.tagChip(added)} disabled={added}
                      aria-label={added ? `${tag} added` : `Add ${tag}`} onClick={() => add([tag])}>
                      {added ? '✓' : '+'} {tag}
                    </button>
                  );
                })}
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                {remaining.length > 0 && (
                  <button type='button' style={S.primary} onClick={() => add(remaining)}>Add all {remaining.length}</button>
                )}
                <span style={S.note}>Based on your caption{photo ? ' and photo' : ''}. Uses 1 AI credit.</span>
              </div>
            </>
          )}

          {aiError && (
            <div style={S.error} role='alert'>
              {aiError.message}
              {aiError.credits && <> <Link to='/dashboard/credits' style={{ fontWeight: 600 }}>Get more AI credits</Link></>}
            </div>
          )}
        </div>
      </div>

      <div style={{ fontSize: 13, color: '#6b7280', margin: '10px 2px 16px' }} data-testid='hashtag-trim-note'>
        {trimOn
          ? <>Sets work for every network. On LinkedIn and Facebook, Greatly Social adds just the first {TRIM_KEEP.linkedin}–{TRIM_KEEP.facebook} so posts don't look spammy (you can change this in </>
          : <>Sets work for every network. Every hashtag goes to LinkedIn and Facebook too (you can change this in </>}
        <button type='button' style={{ ...S.link, fontSize: 13, fontWeight: 500 }} onClick={() => setShowManager(true)}>Settings</button>).
      </div>

      <HashtagSetManager
        show={showManager}
        onHide={() => setShowManager(false)}
        sets={sets}
        setSets={setSets}
        available={available}
        settings={settings}
        onSettingsChange={onSettingsChange}
      />
    </>
  );
}
